import { useState, useMemo, useCallback } from 'react';
import ReactECharts from 'echarts-for-react';
import cn from 'classnames';
import type { EChartsOption } from 'echarts';
import type { ChartBlock as ChartBlockType } from '../types';
import { BlockEmptyState } from './BlockEmptyState';

// ─────────────────────────────────────────────
// 设计稿颜色常量
// ─────────────────────────────────────────────
const COLOR_GREEN = 'rgba(52, 211, 153, 0.8)';
const COLOR_VALUE = '#A3A3A3';
const COLOR_LABEL = '#737373';
const COLOR_GRID = '#E5E5E5';
const COLOR_POINT_FILL = '#FFFFFF';

// ECharts callback params（兼容各版本，取实际回调参数结构的公共子集）
type EChartsCallbackParams = {
  name?: string;
  value?: unknown;
  seriesName?: string;
  dataIndex?: number;
};

// ─────────────────────────────────────────────
// ChartBlock — 数据图表（Bar / Line，ECharts 实现）
// Figma: 灰底卡片 + Y轴单位 + Bar/Line 切换 Tab
// ─────────────────────────────────────────────
export function ChartBlock({ block }: { block: ChartBlockType }) {
  const { chart_type: chartType = 'bar', title, unit, data: rawData } = block;
  const data = useMemo(() => rawData ?? [], [rawData]);
  const [activeTab, setActiveTab] = useState<'bar' | 'line'>(chartType);

  const labels = useMemo(() => data.map(d => d.label.replace(/^\d{4}-/, '').replace(/^0/, '')), [data]);
  const values = useMemo(() => data.map(d => d.value), [data]);

  /**
   * 动态计算 x 轴 label 旋转角度：
   * - 当最长 label 字符数 × 估算字宽 > 每格平均宽度（图表宽 / label 数）时触发旋转
   * - 字符数 > 8 → -45°；字符数 > 5 → -35°；否则不旋转
   */
  const xAxisLabelRotate = useMemo(() => {
    const maxLen = labels.length > 0 ? Math.max(...labels.map(l => l.length)) : 0;
    const count = labels.length;
    // 估算每个 label 可分配的宽度（假设图表区域约 300px，含 containLabel）
    const avgSlotWidth = count > 0 ? 300 / count : 300;
    const estTextWidth = maxLen * 6; // 10px fontSize，单字符约 6px
    if (estTextWidth > avgSlotWidth) {
      return maxLen > 8 ? -45 : -35;
    }
    return 0;
  }, [labels]);

  /**
   * 根据首尾 value 的字符宽度估算折线图左右最小 padding，
   * 防止 label 文字在边缘被截断。
   * 每个字符约占 6px（fontSize=10），额外加 symbol 半径 4px 与安全余量 4px。
   */
  const lineGridPadding = useMemo(() => {
    const charWidth = 6; // 单字符估算宽度（px）
    const symbolRadius = 4; // symbolSize / 2
    const safeMargin = 4;
    const firstLen = String(values[0] ?? '').length;
    const lastLen = String(values[values.length - 1] ?? '').length;
    const left = Math.max(8, Math.ceil((firstLen * charWidth) / 2) + symbolRadius + safeMargin);
    const right = Math.max(8, Math.ceil((lastLen * charWidth) / 2) + symbolRadius + safeMargin);
    return { left, right };
  }, [values]);

  const barOption = useMemo<EChartsOption>(
    () => ({
      grid: {
        top: 24,
        bottom: xAxisLabelRotate !== 0 ? 8 : 20,
        left: 4,
        right: 4,
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: labels,
        axisTick: { show: false },
        axisLine: { show: false },
        axisLabel: {
          color: COLOR_LABEL,
          fontSize: 10,
          margin: 6,
          rotate: xAxisLabelRotate,
          overflow: 'truncate',
          width: xAxisLabelRotate !== 0 ? 80 : undefined,
        },
      },
      yAxis: {
        type: 'value',
        show: false,
      },
      series: [
        {
          type: 'bar',
          data: values,
          barMaxWidth: 26,
          itemStyle: {
            color: COLOR_GREEN,
            borderRadius: [2, 2, 0, 0],
          },
          label: {
            show: true,
            position: 'top',
            color: COLOR_VALUE,
            fontSize: 10,
            formatter: (params: EChartsCallbackParams) => String((params.value as number | null | undefined) ?? ''),
          },
        },
      ],
    }),
    [labels, values, xAxisLabelRotate]
  );

  const lineOption = useMemo<EChartsOption>(
    () => ({
      grid: {
        top: 24,
        bottom: xAxisLabelRotate !== 0 ? 8 : 20,
        left: lineGridPadding.left,
        right: lineGridPadding.right,
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: labels,
        axisTick: { show: false },
        axisLine: { show: false },
        axisLabel: {
          color: COLOR_LABEL,
          fontSize: 10,
          margin: 6,
          rotate: xAxisLabelRotate,
          overflow: 'truncate',
          width: xAxisLabelRotate !== 0 ? 80 : undefined,
        },
        boundaryGap: false,
      },
      yAxis: {
        type: 'value',
        show: true,
        splitLine: {
          lineStyle: {
            color: COLOR_GRID,
            width: 1,
          },
        },
        axisLabel: { show: false },
        axisTick: { show: false },
        axisLine: { show: false },
        splitNumber: 3,
      },
      series: [
        {
          type: 'line',
          data: values,
          smooth: false,
          symbol: 'circle',
          symbolSize: 8,
          itemStyle: {
            color: COLOR_POINT_FILL,
            borderColor: COLOR_GREEN,
            borderWidth: 2,
          },
          lineStyle: {
            color: COLOR_GREEN,
            width: 2.5,
          },
          label: {
            show: true,
            position: 'top',
            color: COLOR_VALUE,
            fontSize: 10,
            formatter: (params: EChartsCallbackParams) => String((params.value as number | null | undefined) ?? ''),
          },
        },
      ],
    }),
    [labels, values, lineGridPadding.left, lineGridPadding.right, xAxisLabelRotate]
  );

  const handleTabClick = useCallback((tab: 'bar' | 'line') => {
    setActiveTab(tab);
  }, []);

  if (data.length === 0) return <BlockEmptyState label="图表" />;

  return (
    <div className="bg-[#F9FAFB] rounded-[8px] p-4 flex flex-col gap-3">
      {/* 标题行 + Tab 切换 */}
      <div className="flex items-start justify-between gap-2">
        {title && <p className="text-[13px] font-medium text-[#09090B] leading-[1.4] flex-1">{title}</p>}
        <div className="flex items-center bg-[#E5E5E5] rounded-[10px] p-[3px] flex-shrink-0">
          {(['bar', 'line'] as const).map(tab => (
            <button
              key={tab}
              type="button"
              onClick={() => handleTabClick(tab)}
              className={cn(
                ' cursor-pointer text-[12px] font-medium px-3 py-1 rounded-[8px] transition-colors',
                activeTab === tab ? 'bg-white text-[#09090B] shadow-sm' : 'text-[#737373] hover:text-[#09090B]'
              )}
            >
              {tab === 'bar' ? 'Bar' : 'Line'}
            </button>
          ))}
        </div>
      </div>

      {/* Y 轴单位标签 */}
      {unit && <span className="text-[11px] text-[#737373]">{unit}</span>}

      {/* ECharts 图表区域 */}
      <ReactECharts
        option={activeTab === 'bar' ? barOption : lineOption}
        style={{ height: 140, width: '100%' }}
        notMerge
        lazyUpdate={false}
      />
    </div>
  );
}
